import EmailSignatureErrorUploadingImagesDialog from '@components/EmailSignaturev2/EmailSignatureDialogs/EmailSignatureErrorUploadingImagesDialog';
import type EmailSignatureImagesUploader from '@components/EmailSignaturev2/EmailSignatureEdit/EmailSignatureImagesUploader';
import type { EmailSignatureV2 } from '@components/EmailSignaturev2/types';
import type { EmailSignaturesActions } from '@reducers/email-signature-reducer';
// es-toolkit/compat: lodash get resolves string/array key paths + default
import { get } from 'es-toolkit/compat';
import { useRouter } from 'next/router';
import { type Dispatch, type FC, useMemo, useState } from 'react';

import { resetWhenSomethingGoneWrong } from '../../utils';
import {
  OnboardingMobileBackButtonSection,
  OnboardingMobileLayout,
  OnboardingMobileProgressBar,
} from '../OnboardingMobileLayout';
import OnboardingContactDetailsPageForMobile from './OnboardingContactDetailsPageForMobile';
import OnboardingNameDetailsPageForMobile from './OnboardingNameDetailsPageForMobile';
import OnboardingOccupationDetailsPageForMobile from './OnboardingOccupationDetailsPageForMobile';
import OnboardingPreviewPageForMobile from './OnboardingPreviewPageForMobile';
import OnboardingSocialLinksPageForMobile from './OnboardingSocialLinksPageForMobile';
import QuickSetupMessage from './QuickSetupMessage';
import { MobileDetailsSteps, type StepInfo } from './types';

function generateStepInfo(normalizedStep: MobileDetailsSteps, stepNumber: number): StepInfo {
  return {
    normalizedStep,
    stepNumber,
  };
}

function normalizeMobileDetailsSteps(mobileDetailsStep: string): StepInfo {
  switch (mobileDetailsStep) {
    case 'details':
      return generateStepInfo(MobileDetailsSteps.NAME_DETAILS, 1);
    case 'occupation-details':
      return generateStepInfo(MobileDetailsSteps.OCCUPATION_DETAILS, 2);
    case 'contact-details':
      return generateStepInfo(MobileDetailsSteps.CONTACT_DETAILS, 3);
    case 'social-links':
      return generateStepInfo(MobileDetailsSteps.SOCIAL_LINKS, 4);
    case 'preview':
      return generateStepInfo(MobileDetailsSteps.PREVIEW, 5);
    case 'sign-up':
      return generateStepInfo(MobileDetailsSteps.SIGN_UP, 5);
    case 'clients':
      return generateStepInfo(MobileDetailsSteps.CLIENTS, 5);
    case 'set-up':
      return generateStepInfo(MobileDetailsSteps.SET_UP, 5);
    default:
      return generateStepInfo(MobileDetailsSteps.NAME_DETAILS, 1);
  }
}

function getPreviousStep(currentStep: MobileDetailsSteps) {
  switch (currentStep) {
    case MobileDetailsSteps.NAME_DETAILS:
      return MobileDetailsSteps.NAME_DETAILS;
    case MobileDetailsSteps.OCCUPATION_DETAILS:
      return MobileDetailsSteps.NAME_DETAILS;
    case MobileDetailsSteps.CONTACT_DETAILS:
      return MobileDetailsSteps.OCCUPATION_DETAILS;
    case MobileDetailsSteps.SOCIAL_LINKS:
      return MobileDetailsSteps.CONTACT_DETAILS;
    case MobileDetailsSteps.PREVIEW:
      return MobileDetailsSteps.SOCIAL_LINKS;
    case MobileDetailsSteps.SIGN_UP:
      return MobileDetailsSteps.PREVIEW;
    default:
      return MobileDetailsSteps.NAME_DETAILS;
  }
}

interface OnboardingDetailsPageForMobileProps {
  handleSave: (
    emailSiganture: EmailSignatureV2,
    onComplete: (savedEmailSignature: EmailSignatureV2) => void,
  ) => Promise<void>;
  uploadEmailSignatureFieldImagesThunk: (
    uploadImage: (emailSignatureImagesUploader: EmailSignatureImagesUploader) => void,
  ) => void;
  onboardingEmailSignature: EmailSignatureV2;
  onboardingEmailSignatureHasLoaded: boolean;
  onboardingEmailSignatureDispatch: Dispatch<EmailSignaturesActions>;
}

const OnboardingDetailsPageForMobile: FC<
  React.PropsWithChildren<OnboardingDetailsPageForMobileProps>
> = ({
  handleSave,
  uploadEmailSignatureFieldImagesThunk,
  onboardingEmailSignature,
  onboardingEmailSignatureHasLoaded,
  onboardingEmailSignatureDispatch,
}) => {
  const router = useRouter();
  const { step } = router.query as {
    step: string[];
  };
  const { normalizedStep: currentNormalisedDetailsStep, stepNumber } = useMemo(() => {
    return normalizeMobileDetailsSteps(get(step, '[0]') as unknown as string);
  }, [step]);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);

  const onGoBack = () => {
    router.push(
      `/onboarding/email-signature/details/${getPreviousStep(currentNormalisedDetailsStep)}`,
      undefined,
      { shallow: true },
    );
  };

  const handleErrorUploadingImage = () => setOpenErrorDialog(true);

  return (
    <OnboardingMobileLayout title={'Create your first email signature'}>
      {stepNumber > 1 && <OnboardingMobileBackButtonSection onBackClick={onGoBack} />}
      {stepNumber < 5 && <OnboardingMobileProgressBar totalSteps={5} currentStep={stepNumber} />}
      {stepNumber === 1 && <QuickSetupMessage />}

      {currentNormalisedDetailsStep === MobileDetailsSteps.NAME_DETAILS && (
        <OnboardingNameDetailsPageForMobile
          emailSignature={onboardingEmailSignature}
          emailSignatureDispatch={onboardingEmailSignatureDispatch}
          handleSave={handleSave}
        />
      )}

      {currentNormalisedDetailsStep === MobileDetailsSteps.OCCUPATION_DETAILS && (
        <OnboardingOccupationDetailsPageForMobile
          emailSignature={onboardingEmailSignature}
          emailSignatureDispatch={onboardingEmailSignatureDispatch}
          handleSave={handleSave}
        />
      )}

      {currentNormalisedDetailsStep === MobileDetailsSteps.CONTACT_DETAILS && (
        <OnboardingContactDetailsPageForMobile
          emailSignature={onboardingEmailSignature}
          emailSignatureDispatch={onboardingEmailSignatureDispatch}
          handleSave={handleSave}
          uploadEmailSignatureFieldImagesThunk={uploadEmailSignatureFieldImagesThunk}
          handleErrorUploadingImage={handleErrorUploadingImage}
        />
      )}

      {currentNormalisedDetailsStep === MobileDetailsSteps.SOCIAL_LINKS && (
        <OnboardingSocialLinksPageForMobile
          emailSignature={onboardingEmailSignature}
          emailSignatureDispatch={onboardingEmailSignatureDispatch}
          handleSave={handleSave}
          uploadEmailSignatureFieldImagesThunk={uploadEmailSignatureFieldImagesThunk}
          handleErrorUploadingImage={handleErrorUploadingImage}
        />
      )}

      {currentNormalisedDetailsStep === MobileDetailsSteps.PREVIEW && (
        <OnboardingPreviewPageForMobile
          emailSignature={onboardingEmailSignature}
          emailSignatureHasLoaded={onboardingEmailSignatureHasLoaded}
        />
      )}
      <EmailSignatureErrorUploadingImagesDialog
        showErrorDialog={openErrorDialog}
        closeDialog={async () => {
          setOpenErrorDialog(false);
          await resetWhenSomethingGoneWrong(router);
        }}
      >
        <span>Please try again shortly</span>
      </EmailSignatureErrorUploadingImagesDialog>
    </OnboardingMobileLayout>
  );
};

export default OnboardingDetailsPageForMobile;
